import { redirect } from 'react-router-dom';

import { AccountResponseDTO } from '_lib/api/apiTypes.shim';
import { AuthUser, PermissionFunction } from '_lib/featureComponents/auth/auth.types';

import { AuthStorage } from '_lib/localStorage/authStorage';

import { logError } from '_lib/utils/logging';

import { appRoutePaths, getFullAppRoutePath } from '../Nav/nav.types';

import { AuthResourceConfig } from './authContext';

type RunPermissionsArgs = {
  user: AuthUser;
  account?: AccountResponseDTO;
  fns: PermissionFunction[];
};

const runPermissions = ({ user, account, fns }: RunPermissionsArgs) => fns.every((fn) => fn({ user, account }));

type CheckResult = { allowed: true } | { allowed: false; redirect: ReturnType<typeof redirect> };

export const authRouteCheck = async (
  authCache: AuthResourceConfig,
  opts?: { permissionFns?: PermissionFunction[] }
): Promise<CheckResult> => {
  try {
    const [authUser, account] = await Promise.all([
      authCache.authUserCacheResource.get(),
      authCache.accountCacheResource.get(),
      authCache.callCenterCacheResource.get()
    ]);

    if (!authUser || !AuthStorage.authToken.get()) {
      authCache.logout();
      return { allowed: false, redirect: redirect('/auth/login') };
    }

    if (opts?.permissionFns) {
      const hasPermissions = runPermissions({ user: authUser, account: account || undefined, fns: opts.permissionFns });
      if (!hasPermissions) return { allowed: false, redirect: redirect(getFullAppRoutePath(appRoutePaths.Forbidden)) };
    }

    return { allowed: true };
  } catch (error) {
    /**
     * NOTE - cache fetching errors are automatically reported, and no other async function here should throw.
     * TLDR; Don't need to worry about reporting, just logout here.
     */
    logError(error);

    authCache.logout();
    return { allowed: false, redirect: redirect('/auth/login') };
  }
};
